import React, { useRef } from 'react';
import { useJsApiLoader, StandaloneSearchBox } from '@react-google-maps/api';
import styled from 'styled-components';

type SearchBoxRef = google.maps.places.SearchBox | null;

interface GooglePlacesSearchProps {
  type: string;
  onAddressSelect: (address: string) => void;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  name: string;
  id: string;
  placeholder?: string;
}
const SearchWrapper = styled.div`
  position: relative;
  width: 100%;

  /* Style the dropdown container */
  .pac-container {
    margin-top: 2px;
    padding: 0.5rem;
    border: none;
    left: -30px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    font-family: inherit;
    width: calc(
      100% + 54px
    ); /* Make dropdown wider than input */ /* Ensure minimum width */
    // z-index: 1000;

    /* Style each suggestion item */
    .pac-item {
      padding: 0.5rem;
      border: none;
      cursor: pointer;
      font-size: 1rem;
      white-space: normal; /* Allow text to wrap */
      line-height: 1.4;

      &:hover {
        background-color: #f8f9fa;
      }
    }

    /* Style the matched text in suggestions */
    .pac-item-query {
      font-size: 1rem;
      font-weight: 300;
      padding-right: 10px;
    }

    /* Remove the default Google Places icons */
    .pac-icon {
      display: none;
    }
  }
`;
const StyledInput = styled.input`
  padding: 0.5rem;
  font-size: 1rem;
  outline: none;
  flex: 1;
  display: flex;
  border: none;
  &:focus {
    outline: none;
    border-color: #007bff;
  }
  &::placeholder {
    font-size: 12px;
    font-weight: 300;
  }
`;

const libraries: ('places' | 'drawing' | 'geometry' | 'visualization')[] = [
  'places',
];

const GooglePlacesSearch: React.FC<GooglePlacesSearchProps> = ({
  type,
  onAddressSelect,
  value,
  onChange,
  onBlur,
  name,
  id,
  placeholder,
}) => {
  const inputRef = useRef<SearchBoxRef>(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_PLACES_API_KEY!,
    libraries: libraries,
  });

  const handlePlacesChanged = () => {
    if (inputRef.current) {
      const places = inputRef.current.getPlaces();
      if (places && places.length > 0) {
        const selectedPlace = places[0];
        const formatted = selectedPlace?.formatted_address || '';
        const cleanedAddress = formatted
          .replace(/^[A-Za-z0-9]+(\+)[A-Za-z0-9]+,?\s*/i, '')
          .trim();

        onAddressSelect(cleanedAddress);
      }
    }
  };

  if (!isLoaded) {
    return (
      <StyledInput
        type={type}
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete="off"
        disabled
      />
    );
  }

  return (
    <SearchWrapper>
      <StandaloneSearchBox
        onLoad={(ref: SearchBoxRef) => {
          inputRef.current = ref;
        }}
        onPlacesChanged={handlePlacesChanged}
      >
        <StyledInput
          type={type}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          autoComplete="off"
          className="w-100"
        />
      </StandaloneSearchBox>
    </SearchWrapper>
  );
};

export default GooglePlacesSearch;
